/* eslint-disable @typescript-eslint/no-explicit-any */
import { AsyncValidatorFn, ValidatorFn } from "@angular/forms";

import { defaultErrorMessages } from "../data/error-messages";
import { ValidationErrorKey } from "../validators/general-validation.";

import { HelpDialog } from "./dialog/form-dialog";

export abstract class FormField {
  public controlType: ControlType = ControlType.INPUT;

  public key!: string;
  public label?: string | undefined;
  public subLabel?: string | undefined;
  public testId?: string | undefined;

  public initialValue?: FormControlValue;
  public hasOwnLabel?: boolean | undefined;

  public expressions?: Expressions | undefined;
  public errorMessages?: Partial<ErrorMessages> = defaultErrorMessages;

  public required?: boolean | undefined;
  public validators?: ValidatorFn[] | undefined;
  public asyncValidators?: AsyncValidatorFn[] | undefined;
  public disabled?: HTMLInputElement["disabled"] | undefined;

  public updateFormControlOn: UpdateFormControlOn = UpdateFormControlOn.CHANGE;

  public isLabelHidden?: boolean | undefined;
  public helpDialog?: HelpDialog | undefined;
  public infoText?: string | undefined;

  public containerClassName?: string;
  public labelClassName?: string;

  constructor(init: FormFieldInit<FormField>) {
    this.key = init.key;
    this.label = init.label;
    this.subLabel = init.subLabel;

    this.testId = init.testId;

    this.initialValue = init.initialValue;

    this.required = init.required;
    this.validators = init.validators ?? [];
    this.asyncValidators = init.asyncValidators ?? [];
    this.disabled = init.disabled;

    this.isLabelHidden = init.isLabelHidden;
    this.helpDialog = init.helpDialog;
    this.infoText = init.infoText;

    this.expressions = init.expressions;

    this.errorMessages = { ...this.errorMessages, ...init.errorMessages };

    this.containerClassName = init.containerClassName ?? ""; // Needs to be initialized as string when passed via ngClass.
    this.labelClassName = init.labelClassName ?? ""; // Needs to be initialized as string when passed via ngClass.
  }

  protected addErrorMessages(errorMessages: Partial<ErrorMessages>): void {
    this.errorMessages = {
      ...this.errorMessages,
      ...errorMessages,
    };
  }
}

export type FormFieldInit<T> = Omit<
  T,
  "controlType" | "updateFormControlOn" | "hasOwnLabel"
>;

export enum UpdateFormControlOn {
  CHANGE = "change",
  BLUR = "blur",
  SUBMIT = "submit",
}

export type FormControlValue = unknown;

type Expressions<T = any> = {
  derive?: DeriveExpressionFn<T>;
  disable?: ExpressionFn<T>;
  hide?: ExpressionFn<T>;
};

type ExpressionFn<T> = (values: T) => boolean;

export enum FormControlType {
  SIMPLE = "Simple",
  ARRAY = "Array",
}

type DeriveExpressionFn<T> = (
  update: SimpleDerivationContext<T> | ArrayDerivationContext<T>,
) => FormControlValue;

export interface SimpleDerivationContext<T = any>
  extends BaseDerivationContext<T> {
  formControlType: FormControlType.SIMPLE;
}

export interface ArrayDerivationContext<T = any>
  extends BaseDerivationContext<T> {
  formControlType: FormControlType.ARRAY;
  index: number;
}

type BaseDerivationContext<T> = {
  // Anything which makes use of the derive expression MUST do a comparison
  // from the 'previousValue' to the 'currentValue' for all the things which you are interested in.
  // If this is not done we run into an endless loop as every value being returned (except null / undefined) triggers
  // another round of expression checks (this gives all the other FormControls a chance to make changes to the new value if necessary)
  // The expressions loop finishes once every expression returned 'undefined' or 'null'.
  previousValue?: T;
  currentValue: T;
  isDirty: boolean;
};

export type ValidationErrorKeyValues =
  (typeof ValidationErrorKey)[keyof typeof ValidationErrorKey];

type ErrorFunction = (configuration: Record<string, number>) => string;
type ErrorMessages = Record<ValidationErrorKeyValues, string | ErrorFunction>;

export enum ControlType {
  // Base
  INPUT = "input",

  // Input
  CHECKBOX = "checkbox",
  CODE = "code",
  RADIO = "radio",
  RANGE = "range",
  SELECT = "select",
  DATE = "date",
  ARRAY = "array",
  TYPEAHEAD = "typeahead",
  TOGGLE = "toggle",
}
